// src/utils/affiches.js
// ─────────────────────────────────────────────────────────────
// Gestion des dates d'événement portées par les affiches.
//
// La date est stockée DANS LE NOM DU FICHIER S3, il n'y a pas de
// table en base pour les photos :
//
//   photos/evenements/2026-09-20__1757012345-soiree.jpg
//                     └────┬────┘
//                     date de l'événement
//
// Conséquence utile : supprimer la photo supprime la date, il n'y a
// jamais de ligne orpheline à nettoyer.
//
// Les photos postées avant cette fonctionnalité n'ont pas de préfixe
// de date : elles sont traitées comme « sans date » et affichées en
// fin de liste, jusqu'à ce qu'on leur en attribue une.
// ─────────────────────────────────────────────────────────────

export const EVENEMENTS_PREFIX = 'photos/evenements/';
export const AMBIANCE_PREFIX   = 'photos/ambiance/';

const DATE_RE = /^(\d{4}-\d{2}-\d{2})__/;

/** Nettoie un nom de fichier pour en faire une clé S3 sûre */
function safeFileName(name) {
  return (name || 'image')
    .replace(/\s+/g, '-')
    .replace(/[^a-zA-Z0-9._-]/g, '');
}

/** 'photos/evenements/2026-09-20__12345-a.jpg' → '2026-09-20' (ou null) */
export function parseEventDate(key) {
  if (typeof key !== 'string') return null;
  const nom = key.split('/').pop() || '';
  const m = nom.match(DATE_RE);
  return m ? m[1] : null;
}

/** Le nom affichable, sans le préfixe de date ni le timestamp */
export function parseLabel(key) {
  if (typeof key !== 'string') return '';
  let nom = key.split('/').pop() || '';
  nom = nom.replace(DATE_RE, '');
  return nom.replace(/^\d{10,}-/, '');
}

/** Construit la clé d'upload, avec date d'événement optionnelle */
export function buildEventKey(prefix, file, dateISO) {
  const base = `${Date.now()}-${safeFileName(file?.name)}`;
  return dateISO ? `${prefix}${dateISO}__${base}` : `${prefix}${base}`;
}

/** Renvoie la clé équivalente portant une nouvelle date (pour un renommage) */
export function withEventDate(key, dateISO) {
  const i = key.lastIndexOf('/');
  const dossier = i === -1 ? '' : key.slice(0, i + 1);
  const nom = (i === -1 ? key : key.slice(i + 1)).replace(DATE_RE, '');
  return dateISO ? `${dossier}${dateISO}__${nom}` : `${dossier}${nom}`;
}

/** Date du jour au format YYYY-MM-DD, en heure locale (pas UTC) */
export function todayISO() {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const j = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${j}`;
}

/**
 * '2026-09-20' → 'dimanche 20 septembre 2026'
 * `locale` (celle de useLangue) écrit la date dans la langue du visiteur ;
 * sans elle, la date reste en français.
 */
export function formatDateFr(dateISO, options, locale = 'fr-FR') {
  if (!dateISO) return '';
  const [y, m, j] = dateISO.split('-').map(Number);
  return new Date(y, m - 1, j).toLocaleDateString(locale, options || {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });
}

/**
 * Répartit les affiches en « à venir » et « passés ».
 *
 * - un événement daté d'aujourd'hui reste « à venir » (il n'a pas
 *   encore eu lieu au moment où quelqu'un consulte la page)
 * - à venir  : du plus proche au plus lointain
 * - passés   : du plus récent au plus ancien
 * - sans date : rangés en fin de « à venir », faute de mieux
 *
 * @param {Array<{key: string}>} items
 */
export function splitEvenements(items) {
  const today = todayISO();
  const aVenir = [];
  const passes = [];
  const sansDate = [];

  for (const item of items || []) {
    const date = parseEventDate(item?.key);
    if (!date)             sansDate.push({ ...item, date: null });
    else if (date < today) passes.push({ ...item, date });
    else                   aVenir.push({ ...item, date });
  }

  // Comparaison lexicographique : valable car le format ISO est trié par nature
  aVenir.sort((a, b) => a.date.localeCompare(b.date));
  passes.sort((a, b) => b.date.localeCompare(a.date));

  return { aVenir: [...aVenir, ...sansDate], passes, sansDate };
}
